import { useEffect, useRef } from 'react';

import { isEditableTarget } from '../utils/isEditableTarget';

const isUndo = (event) => event.code === 'KeyZ' && !event.shiftKey;
const isRedo = (event) => event.code === 'KeyY' || (event.code === 'KeyZ' && event.shiftKey);
const isDelete = (event) => event.key === 'Delete' || event.key === 'Backspace';
const isDuplicate = (event) => event.code === 'KeyD' && !event.shiftKey;
const isSelectAll = (event) => event.code === 'KeyA' && !event.shiftKey;

const useKeyboardShortcuts = ({
  undoRedo,
  deletion,
  duplication = false,
  selection = false,
  undo,
  redo,
  deleteSelected,
  duplicateSelected,
  selectAll,
}) => {
  const actionsRef = useRef({ undo, redo, deleteSelected, duplicateSelected, selectAll });
  actionsRef.current = { undo, redo, deleteSelected, duplicateSelected, selectAll };

  useEffect(() => {
    if (!undoRedo && !deletion && !duplication && !selection) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (isEditableTarget(event.target) || event.altKey) {
        return;
      }
      const withModifier = event.metaKey || event.ctrlKey;
      const actions = actionsRef.current;

      if (undoRedo && withModifier && (isUndo(event) || isRedo(event))) {
        event.preventDefault();
        if (isUndo(event)) {
          actions.undo();
        } else {
          actions.redo();
        }
        return;
      }

      if (duplication && withModifier && isDuplicate(event)) {
        event.preventDefault();
        actions.duplicateSelected();
        return;
      }

      if (selection && withModifier && isSelectAll(event)) {
        event.preventDefault();
        actions.selectAll();
        return;
      }

      if (deletion && !withModifier && isDelete(event)) {
        event.preventDefault();
        actions.deleteSelected();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [undoRedo, deletion, duplication, selection]);
};

export default useKeyboardShortcuts;
